import { useEffect, useState, type MutableRefObject } from 'react';
import { ViewTurnCorner } from './ViewTurnCorner';
import { MM_PER_CM } from './axisGrid';

// THE VIEWER'S ONE CONTROL: home, a turn about each axis, and how far off square the shot is
// about each of those same axes — in a single box in the corner.
//
// They were two boxes. The letters X, Y and Z sat in one and the numbers about X, Y and Z sat
// in the other, in the same three colours, a few pixels apart: the same three axes written
// twice, and neither box saying what the other was for. Under one letter, a number is a
// reading of that arrow; beside it, it is a second control you have to pair up by eye.
//
// WHY THIS WRAPPER EXISTS AT ALL, rather than the phase passing the angles straight down: the
// angles change every frame of an orbit. Held in the phase they would re-render eleven
// hundred lines of selectors, chips and panels sixty times a second to move three digits. The
// scene writes into the ref, this reads it, and the re-render stops at this subtree.
export type ViewState = {
  deg: [number, number, number];
  zoom: number;
  centre: [number, number, number];
};

export type ViewAnglesSink = MutableRefObject<((view: ViewState) => void) | null>;

export function ViewControls({
  sink, onHome, onTurn, onLook, homeLabel, gizmoOn, onToggleGizmo,
}: {
  sink: ViewAnglesSink;
  onHome: () => void;
  onTurn: (axis: 'x' | 'y' | 'z', degrees: number) => void;
  // PUT THE SHOT THERE — the six numbers the corner prints, typed in. In MILLIMETRES and
  // degrees, which is what the scene is written in; the corner speaks the centimetres it
  // shows and the conversion happens here, at the edge, the way every other reading in this
  // phase converts. See axisGrid.
  //
  // The zoom is this wrapper's to add: the corner never had it to give back, and a shot is
  // an aim, an orientation and a distance. Whatever the wheel has left it at is kept.
  onLook?: (view: {
    centre: [number, number, number]; deg: [number, number, number]; zoom: number;
  }) => void;
  homeLabel?: string;
  gizmoOn?: boolean;
  onToggleGizmo?: () => void;
}) {
  const [view, setView] = useState<ViewState>({ deg: [0, 0, 0], zoom: 1, centre: [0, 0, 0] });

  useEffect(() => {
    sink.current = setView;
    return () => { sink.current = null; };
  }, [sink]);

  return (
    <ViewTurnCorner
      onHome={onHome}
      onTurn={onTurn}
      onLook={onLook && (({ centre, deg }) => onLook({
        centre: centre.map((cm) => cm * MM_PER_CM) as [number, number, number],
        deg,
        zoom: view.zoom,
      }))}
      homeLabel={homeLabel}
      gizmoOn={gizmoOn}
      onToggleGizmo={onToggleGizmo}
      angles={view.deg}
      centre={view.centre}
      zoom={view.zoom}
    />
  );
}
